/* MonthlyRevenueStack — stacked monthly revenue bar chart, reused on
 * the Client / Industry / Service detail pages.
 *
 * Pure transformation: takes projects (with starts/ends/volume/tag
 * arrays) + a lookup of tag id → display name, and produces the
 * interactive-chart spec for an HTML stacked bar chart with 18
 * months of bars on the X axis and one stack segment per tag
 * (service for Client view, client for Industry/Service view).
 *
 * Revenue split policy (matches Phase 0 decision): a project's
 * volume_chf is distributed *proportionally* across the months it
 * spans — €120k over 12 months = €10k/month. Projects with an
 * unbounded end date stop accruing at "today". Projects with no
 * dates contribute their volume to the START month (one bucket)
 * or, if no starts_at either, the most-recent month.
 *
 * Each project's monthly amount is then split equally across the
 * tags it carries. A project with 2 services tagged contributes
 * 50/50 to each service. Untagged → "Unattributed" bucket.
 */
import React from 'react';
import { InteractiveChart } from './interactive-chart.jsx';

const DEFAULT_WINDOW_MONTHS = 18;
const UNATTRIBUTED = 'Unattributed';

/** Build a "YYYY-MM" key from a year + zero-indexed month. */
function ym(year, monthIdx) {
  return `${year}-${String(monthIdx + 1).padStart(2, '0')}`;
}

/** Build the array of last N "YYYY-MM" labels ending with the current
 *  month. e.g. [..., "2025-04", "2025-05", "2025-06"]. */
function buildWindow(now, months) {
  const out = [];
  const start = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  for (let i = 0; i < months; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    out.push(ym(d.getFullYear(), d.getMonth()));
  }
  return out;
}

/** Months elapsed between two timestamps (inclusive of start, exclusive
 *  of end). Floor to 1 so a single-day project still gets one month. */
function monthsBetween(startMs, endMs) {
  const s = new Date(startMs);
  const e = new Date(endMs);
  const diff = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  return Math.max(1, diff);
}

/** Iterate every "YYYY-MM" key from startMs up to (and including)
 *  endMs. */
function* monthKeys(startMs, endMs) {
  const s = new Date(startMs);
  const e = new Date(endMs);
  let year = s.getFullYear();
  let month = s.getMonth();
  while (year < e.getFullYear() || (year === e.getFullYear() && month <= e.getMonth())) {
    yield ym(year, month);
    month++;
    if (month > 11) { month = 0; year++; }
  }
}

/**
 * Compute the stacked-bar data for a list of projects.
 *
 * @param {Array}   projects   Project rows with { startsAt, endsAt,
 *                             volumeChf, [stackKey]: string[] }.
 * @param {string}  stackKey   Which array field on each project holds
 *                             the tag IDs we want to stack by — e.g.
 *                             'services' for the Client view.
 * @param {Object}  tagLookup  Map of tag id → human-readable label.
 * @param {number}  months     Window length in months.
 * @returns                    { data, stacks } ready for interactive-chart.
 */
export function buildMonthlyRevenueData(projects, stackKey, tagLookup, months = DEFAULT_WINDOW_MONTHS) {
  const now = new Date();
  const window = buildWindow(now, months);
  const windowSet = new Set(window);

  // bucket[monthKey][stackLabel] = number
  const bucket = Object.create(null);
  for (const m of window) bucket[m] = Object.create(null);

  // To color stacks deterministically we collect stack labels in
  // first-seen order.
  const seenStacks = new Set();

  for (const p of (projects || [])) {
    // Per-month CHF for this project. Prefer the authoritative series the
    // API now attaches (`monthlyRevenueChf` — stored monthly revenue, or a
    // working-day spread of volume when no table exists). Fall back to the
    // legacy equal-month spread of volumeChf for any caller not yet passing
    // the series.
    let monthly = null;
    if (p.monthlyRevenueChf && typeof p.monthlyRevenueChf === 'object') {
      monthly = p.monthlyRevenueChf;
    } else {
      const vol = Number(p.volumeChf || 0);
      if (Number.isFinite(vol) && vol > 0) {
        const startsAt = p.startsAt ? new Date(p.startsAt).getTime() : null;
        const endsAt   = p.endsAt   ? new Date(p.endsAt).getTime()   : null;
        const projStart = startsAt ?? endsAt ?? now.getTime();
        const projEnd   = endsAt   ?? now.getTime();
        const effectiveEnd = Math.min(projEnd, now.getTime());
        const effectiveStart = Math.min(projStart, effectiveEnd);
        const perMonth = vol / monthsBetween(effectiveStart, effectiveEnd);
        monthly = {};
        for (const key of monthKeys(effectiveStart, effectiveEnd)) monthly[key] = perMonth;
      }
    }
    if (!monthly) continue;

    // Tags this project's revenue stacks into. The stackKey field
    // can be an array (multi-tag, e.g. project.services) or a string
    // (single-tag, e.g. project.clientId). Empty → Unattributed.
    const raw = p[stackKey];
    const tagIds = Array.isArray(raw)
      ? raw
      : (typeof raw === 'string' && raw ? [raw] : []);
    const tagLabels = tagIds.length
      ? tagIds.map(id => tagLookup[id] || 'Other').filter(Boolean)
      : [UNATTRIBUTED];

    for (const [key, amt] of Object.entries(monthly)) {
      if (!windowSet.has(key)) continue;     // outside the visible window
      const perTag = Number(amt || 0) / tagLabels.length;
      if (!perTag) continue;
      for (const label of tagLabels) {
        bucket[key][label] = (bucket[key][label] || 0) + perTag;
        seenStacks.add(label);
      }
    }
  }

  // Round to whole CHF (the volume scale is whole units in the schema)
  // and assemble the data rows.
  const stacks = Array.from(seenStacks);
  const data = window.map(monthKey => {
    const row = { name: monthKey };
    for (const s of stacks) row[s] = Math.round(bucket[monthKey][s] || 0);
    return row;
  });

  return { data, stacks };
}

/**
 * <MonthlyRevenueStack /> — the rendered chart. Takes the same inputs
 * as buildMonthlyRevenueData plus a chart title.
 */
export const MonthlyRevenueStack = ({
  title,
  projects,
  stackKey,
  tagLookup,
  months = DEFAULT_WINDOW_MONTHS,
  footnote,
}) => {
  const { data, stacks } = React.useMemo(
    () => buildMonthlyRevenueData(projects, stackKey, tagLookup, months),
    [projects, stackKey, tagLookup, months],
  );

  const hasAnyValue = data.some(row => stacks.some(s => row[s] > 0));
  if (!hasAnyValue) {
    return (
      <div style={{padding:'18px 0',color:'var(--fg-muted)',fontSize:13}}>
        No revenue to plot. Add `volume_chf` + dates on at least one project (Admin → Projects → Commercials) and this chart will populate.
      </div>
    );
  }

  return (
    <InteractiveChart
      spec={{
        type: 'bar',
        title,
        data,
        xKey: 'name',
        stacked: true,
        series: stacks,
        height: 280,
        footnote: footnote || 'Monthly run-rate, proportional split across each project\'s active months. Untagged revenue lands in "Unattributed".',
      }}
    />
  );
};
